import { useState } from 'react';

import { useGoogleLogin } from '@react-oauth/google';
import GoogleButton from 'react-google-button';


import { AuthLayout } from '../catalyst/auth-layout';
import { Button } from '../catalyst/button';
import InputWithError from '../catalyst/custom/inputWithError';
import { ErrorMessage, Field } from '../catalyst/fieldset';
import { Heading } from '../catalyst/heading';
import { Strong, Text, TextLink } from '../catalyst/text';
import { Logo } from '../logo';

import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import Spinning from '../components/spinning';


import CheckCircleIcon from '@heroicons/react/24/solid/CheckCircleIcon';

import config from '../config';



export function Terms(props) {
  return (
    <Text variant="body2" color="text.secondary" align="center" {...props}>
      {'By proceeding, you agree to '}
      <TextLink color="inherit" sx={{ fontWeight: 'bold', textDecoration: 'underline' }} href={config.termsPath}>
        our terms of service and privacy policy 
      </TextLink>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Text>
  );
}

// TODO remove, this demo shouldn't need to reset the theme.

function SignUpForm({ 
  name, setName, 
  email, setEmail, 
  password, setPassword, 
  confirmPassword, setConfirmPassword, 
  errors, errResponse, 
  handleSubmit, submitting, 
  googleLogin, t 
}) {
  return (
    <div className="grid w-full max-w-sm grid-cols-1 gap-8">
      <div className="flex flex-col items-center justify-center space-y-4">
        <Logo className="h-6 text-zinc-950 dark:text-white forced-colors:text-[CanvasText]" redirectHome={true}/>
        <GoogleButton onClick={() => googleLogin()} label="Login with Google"></GoogleButton>
        <Heading>{t("or")}</Heading>
        <Heading>{t("title")}</Heading>
        {/* <LanguageSelector></LanguageSelector> */}
      </div>

      
      <InputWithError error={errors.name} type="name" name="name" value={name} label={t("name")} onChange={(e) => setName(e.target.value)}/>
      <InputWithError error={errors.email} type="email" name="email" value={email} label={t("email")} onChange={(e) => setEmail(e.target.value)}/>
      <InputWithError error={errors.password} type="password" name="password" value={password} label={t("password")} onChange={(e) => setPassword(e.target.value)}/>
      <InputWithError error={errors.confirmPassword} type="password" name="confirmPassword" value={confirmPassword} label={t("confirmPassword")} onChange={(e) => setConfirmPassword(e.target.value)}/>

      {errResponse && <Field><ErrorMessage>{errResponse}</ErrorMessage></Field>}


      <Button onClick={handleSubmit} className="w-full cursor-pointer"  disabled={submitting}>
          {submitting ? <Spinning /> : t("signup")}
      </Button>

      <div className="flex items-center justify-between">
        <Text className="ml-auto">
            {t("alreadyHaveAnAccount")}
            {' '}
            <TextLink href="login">
                <Strong>{t("login")}</Strong>
            </TextLink>
        </Text>
      </div>
      <Terms></Terms>
    </div>
  )
}

function SignupComfirmation({ email }) {
  return (
    <div className="grid w-full max-w-sm grid-cols-1 gap-8">
      <div className="flex flex-col items-center justify-center space-y-4">
        <Logo className="h-6 text-zinc-950 dark:text-white forced-colors:text-[CanvasText]" redirectHome={true}/>
        <Heading>Account activation email sent</Heading>
        <CheckCircleIcon className="h-30 w-30 text-green-500" aria-hidden="true" />

        <Text>
          We've emailed you a link to verify your email <Strong>{email}</Strong>. Please check your inbox.
        </Text>
      </div>
    </div>
  );
}

export default function SignUp() {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [errResponse, setErrResponse] = useState("");

  const { t } = useTranslation(undefined, { keyPrefix: 'signup' });
  // const {greeting} = t("greeting", {channel: "RoadsideCoder"});

  const [submitting, setSubmitting] = useState(false)

  const [sentSuccessfully, setSentSuccessfully] = useState(false)


  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(true);

  const validate = (submittedFormData) => {
    let errors = {};

    if (!submittedFormData.name) {
      errors.name = t("nameRequired");
    }

    if (!submittedFormData.email) {
      errors.email = t("emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(submittedFormData.email)) {
      errors.email = t("emailInvalid");
    }

    if (!submittedFormData.password) {
      errors.password = t("passwordRequired");
    } else if (submittedFormData.password.length < 6) {
      errors.password = t("passwordLengthError");
    }

    if (!submittedFormData.confirmPassword) {
      errors.confirmPassword = t("confirmRequired");
    } else if (submittedFormData.confirmPassword !== submittedFormData.password) {
      errors.confirmPassword = t("passwordsNotMatched");
    }

    return errors;
  };

  const handleSubmit = () => {

    var newData = {
      name: name,
      email: email,
      password: password,
      confirmPassword: confirmPassword,
      acceptedTerms: true
    }
    const validationErrors = validate(newData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      return;
    } else {
      setErrors({});
      setSubmitting(true)
      fetch(config.apiUrl + "/user/create", { // Replace with your actual endpoint
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newData)
      })
        .then(response => {
          setSubmitting(false)
          if (!response.ok) {
            if (response.status === 400) {
              return response.text().then(text => {
                throw new Error(t(text));
              });
            }
            throw new Error(t("registerError"));
          }
          return response.text();
        })
        .then(data => {
          setErrResponse(t(data));
          setSentSuccessfully(true)
          setErrors({});
        })
        .catch((error) => {
          setSubmitting(false)
          setErrResponse(error.message)
          setErrors({});
          console.error('Error:', error);
        });
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async tokenResponse => {
      navigate("/login/google/authenticate?code=" + tokenResponse.access_token)
    },
    // flow: 'implicit', // implicit is the default
  });

  return (
    <AuthLayout>
      {!sentSuccessfully ? (
        <SignUpForm 
          name={name} setName={setName}
          email={email} setEmail={setEmail}
          password={password} setPassword={setPassword}
          confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
          errors={errors} errResponse={errResponse}
          handleSubmit={handleSubmit} submitting={submitting}
          googleLogin={googleLogin} t={t}
        />
      ) : (
        <SignupComfirmation email={email} />
      )}
    </AuthLayout>
  );
}